/*
  Rationale: This heavy Three.js module lives in the theme's src bundle and is
  lazy-loaded only when the block is present. The block-level view.js remains
  a tiny initializer (declared via block.json viewScript) that calls into the
  theme (window.wsInitModel3DSectionIn), which imports this module on demand.
  This keeps editor previews fast and avoids shipping Three.js in every block script.
*/
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import * as THREE from 'three';
import { OutlineEffect } from 'three/examples/jsm/effects/OutlineEffect.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export function initModel3DSection(rootEl) {
  if (!rootEl) return;

  let camera, scene, renderer, effect;
  let canvasRoot = null;
  let isAnimating = false; // start paused; resume only when in view
  let hasInitialized = false;
  let visibilityState = false;
  let autoSequenceEnabled = true;
  let sequenceTimerId = null;
  const isInOffcanvas = !!(rootEl && rootEl.closest && rootEl.closest('.offcanvas'));
  // Swiper v11 modules are provided via the `modules` option
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  // Interactive meshes (np. litery "W" i "S"); część "/" będzie pomijana
  let meshes = [];
  let slideCount = 0;
  let modelRef = null;
  let modelCenter = new THREE.Vector3(0,0,0);
  let modelRadius = 1;
  // Pointer-driven camera orbit state
  let hoveredMesh = null;
  let activeMesh = null;
  // Kolory interakcji: bazowo czarny model, hover ciemnoszary, klik – czysty biały
  const HOVER_HEX = 0x555555;   // ciemniejszy szary dla hover
  const ACTIVE_HEX = 0xffffff;  // pełny biały dla klikniętego (active)
  let targetModelCenter = modelCenter.clone();
  let pointerTargetX = 0;
  let pointerTargetY = 0;
  let pointerX = 0;
  let pointerY = 0;
  const pointerLerp = 0.08;
  const maxHorizontalAngle = Math.PI / 2; // how far we can rotate horizontally with the mouse 
  const maxVerticalAngle = Math.PI / 1; // how far we can tilt vertically with the mouse
  let baseCameraRadius = 1;
  let baseCameraPhi = Math.PI / 2;
  let baseCameraTheta = 0;
  let targetBaseCameraRadius = 1;
  let targetBaseCameraPhi = Math.PI / 2;
  let targetBaseCameraTheta = 0;
  const centerLerp = 0.06;
  let pointerTargetEl = null;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const inView = entry.isIntersecting && entry.intersectionRatio > 0.1;
      if (!hasInitialized && inView) {
        init();
        hasInitialized = true;
        setVisible(true);
      } else if (hasInitialized) {
        setVisible(inView);
      }
    });
  }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
  observer.observe(rootEl);

  function init() {
    canvasRoot = rootEl.querySelector('.js-model3d-section__canvas') || rootEl;
    const width = canvasRoot.clientWidth || 800;
    const height = canvasRoot.clientHeight || 500;

    camera = new THREE.PerspectiveCamera(45, width / height, 0.25, 50);
    camera.position.set(-1.8, 20, 2.7);

    scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff));
    const pointLight = new THREE.PointLight(0xffffff, 100);
    camera.add(pointLight);
    scene.add(camera);

    // Guard: ensure WebGL is available before creating renderer
    if (!isWebGLAvailable()) {
      return; // gracefully skip if context cannot be created
    }
    // Try creating a high-quality renderer first, then fall back to safer options
    var created = false;
    var optionsList = [
      { antialias: true, alpha: true, powerPreference: 'default', failIfMajorPerformanceCaveat: false },
      { antialias: true, alpha: true, powerPreference: 'low-power', failIfMajorPerformanceCaveat: false },
      { antialias: false, alpha: true, powerPreference: 'low-power', failIfMajorPerformanceCaveat: false }
    ];
    for (var i = 0; i < optionsList.length && !created; i++) {
      try {
        renderer = new THREE.WebGLRenderer(optionsList[i]);
        var dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
        renderer.setPixelRatio(dpr);
        renderer.setSize(width, height);
        canvasRoot.appendChild(renderer.domElement);
        created = true;
      } catch (e) {
        renderer = null;
      }
    }
    if (!created) return; // bail if renderer creation fails entirely
    // Smooth fade for canvas element
    try {
      renderer.domElement.style.opacity = '0';
      renderer.domElement.style.transition = 'opacity 300ms ease';
    } catch (e) {}

    effect = new OutlineEffect(renderer);

    // Controls intentionally disabled to avoid user interactions (scroll/drag)

    const loader = new GLTFLoader();
    // Fallback to original theme asset if no override provided
    const modelUrl = (window.wsModel3D && window.wsModel3D.url) || '/wp-content/themes/ws-theme/blocks/model3d-section/ws2.gltf';
    if (!modelUrl) {
      // No model configured; just render empty scene
      render();
      bindEvents();
      return;
    }

    loader.load(modelUrl, async (gltf) => {
      const model = gltf.scene;
      await renderer.compileAsync(model, camera, scene);

      // Ustawiamy jednolity czarny materiał i obrys; tylko wybrane części (W, S)
      // będą interaktywne – znak "/" pomijamy na podstawie nazw węzłów.
      model.traverse((child) => {
        if (child.isMesh) {
          const isSlash = isSlashMesh(child);

          // Materiał + obrys ustawiamy dla wszystkich części modelu
          child.material = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.8 });
          // Remember base color so we can return from hover/active states
          child.currentHex = child.material.color.getHex();
          const edges = new THREE.EdgesGeometry(child.geometry, 10);
          const line = new THREE.LineSegments(
            edges,
            new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35 })
          );
          // Ignore edge lines for raycasting, żeby klik/hover łapał bryłę, a nie kontur
          try { line.raycast = function() { return null; }; } catch (e) {}
          child.add(line);

          // Do tablicy interaktywnych meshów dodajemy tylko litery "W" i "S"
          if (!isSlash) {
            meshes.push(child);
          }
        }
      });

      scene.add(model);
      model.scale.setScalar(1);
      model.scale.y = 4;
      model.rotation.x = 0;
      model.rotation.y = 0;
      modelRef = model;
      fitCameraToModel(camera, renderer, model);

      // Cache base camera spherical coordinates around the model center
      const offset = camera.position.clone().sub(modelCenter);
      const spherical = new THREE.Spherical().setFromVector3(offset);
      baseCameraRadius = spherical.radius || 1;
      baseCameraPhi = spherical.phi;
      baseCameraTheta = spherical.theta;
      targetBaseCameraRadius = baseCameraRadius;
      targetBaseCameraPhi = baseCameraPhi;
      targetBaseCameraTheta = baseCameraTheta;
      targetModelCenter.copy(modelCenter);

      // Disable camera auto-sequence inside offcanvas by default
      if (isInOffcanvas) {
        autoSequenceEnabled = false;
      }
      // Disable automatic camera flight sequence; camera will now follow mouse position instead
      // Kick render only when visible; observer will handle visibility
      if (visibilityState) {
        startRendering();
      }
      bindEvents();
      // Initialize Swiper once model is ready
      initSwiper();
      // Enable hover-driven interaction in offcanvas
      initOffcanvasHover();
    });
  }

  function bindEvents() {
    window.addEventListener('resize', onResize);
    // Pointer events should be bound to the actual WebGL canvas if available,
    // so that coordinates used for raycasting match rendered content exactly.
    pointerTargetEl = (renderer && renderer.domElement) || canvasRoot;
    if (pointerTargetEl) {
      pointerTargetEl.addEventListener('pointermove', onPointerMove);
      pointerTargetEl.addEventListener('pointerleave', onPointerLeave);
      pointerTargetEl.addEventListener('pointerdown', onPointerDown);
    }
  }

  function onResize() {
    const width = (canvasRoot && canvasRoot.clientWidth) || rootEl.clientWidth || 800;
    const height = (canvasRoot && canvasRoot.clientHeight) || rootEl.clientHeight || 500;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
    if (modelRef) fitCameraToModel(camera, renderer, modelRef, { maintainAngle: true });
    // Guard legacy helper if present in global scope
    try { if (typeof syncItemsHeight === 'function') syncItemsHeight(); } catch (e) {}
  }

  function onPointerMove(event) {
    if (!pointerTargetEl) return;
    const rect = pointerTargetEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;  // [-1, 1]
    const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;  // [-1, 1]
    pointerTargetX = x;
    pointerTargetY = y;
    mouse.x = x;
    mouse.y = -y; // Three.js NDC has Y flipped
    // Update hovered mesh for subtle highlight
    if (camera && modelRef) {
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObject(modelRef, true);
      let hit = intersects && intersects.length ? intersects[0].object : null;
      // Ignoruj elementy znaku "/" na podstawie struktury/nazw węzłów
      if (hit && isSlashMesh(hit)) {
        hit = null;
      }
      setHoveredMesh(hit);
    }
  }

  function onPointerLeave() {
    // Gently return camera to the base position when the pointer leaves the canvas
    pointerTargetX = 0;
    pointerTargetY = 0;
    setHoveredMesh(null);
  }

  function onPointerDown(event) {
    if (!pointerTargetEl || !camera || !renderer || !modelRef) return;
    const rect = pointerTargetEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    mouse.x = x;
    mouse.y = -y;
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(modelRef, true);
    if (!intersects || !intersects.length) return;
    const hit = intersects[0].object;
    if (!hit || isSlashMesh(hit)) return;
    focusOnMesh(hit);
  }

  // Hover highlighting disabled per requirements

  function render() {
    // Orbit camera around the (possibly re-centered) model center based on pointer position
    if (modelRef) {
      // Smoothly move camera base/orbit center towards targets
      modelCenter.lerp(targetModelCenter, centerLerp);
      baseCameraRadius += (targetBaseCameraRadius - baseCameraRadius) * centerLerp;
      baseCameraPhi += (targetBaseCameraPhi - baseCameraPhi) * centerLerp;
      baseCameraTheta += (targetBaseCameraTheta - baseCameraTheta) * centerLerp;

      // Smoothly interpolate towards target pointer position
      pointerX += (pointerTargetX - pointerX) * pointerLerp;
      pointerY += (pointerTargetY - pointerY) * pointerLerp;

      // Apply pointer offsets to camera angles for orbiting effect
      const clampedPhi = THREE.MathUtils.clamp(
        baseCameraPhi + pointerY * maxVerticalAngle,
        0.1,
        Math.PI - 0.1
      );
      const theta = baseCameraTheta + pointerX * maxHorizontalAngle;

      const spherical = new THREE.Spherical(baseCameraRadius, clampedPhi, theta);
      const newPos = new THREE.Vector3().setFromSpherical(spherical).add(modelCenter);
      camera.position.copy(newPos);
      camera.lookAt(modelCenter);
    }
    effect.render(scene, camera);
    if (isAnimating) requestAnimationFrame(render);
  }

  function startRendering() {
    if (!isAnimating) {
      isAnimating = true;
      try { if (renderer && renderer.domElement) renderer.domElement.style.opacity = '1'; } catch (e) {}
      requestAnimationFrame(render);
      controlSwiper('start');
    }
  }

  function stopRendering() {
    if (isAnimating) {
      isAnimating = false;
      try { if (renderer && renderer.domElement) renderer.domElement.style.opacity = '0'; } catch (e) {}
      controlSwiper('stop');
    }
  }

  function setVisible(inView) {
    visibilityState = !!inView;
    if (!hasInitialized) return;
    if (visibilityState) startRendering(); else stopRendering();
  }

  function animateCameraSequence(cam) {
    if (!autoSequenceEnabled) return;
    const baseDirs = [
      new THREE.Vector3(0, 1, 0.3),
      new THREE.Vector3(0.25, 0.85, 0.4),
      new THREE.Vector3(-0.8, 0.8, 0.2),
      new THREE.Vector3(-0.9, 0.3, -0.5),
      new THREE.Vector3(-0.2, 1.2, -0.4)
    ].map(v => v.normalize());
    let i = 0;
    function step() {
      if (!autoSequenceEnabled) return;
      i = (i + 1) % baseDirs.length;
      const required = getRequiredDistance(camera, renderer, modelRadius);
      const next = modelCenter.clone().add(baseDirs[i].clone().multiplyScalar(required));
      // simple lerp without gsap
      const durationMs = 3000;
      const start = performance.now();
      const startPos = cam.position.clone();
      const tick = (t) => {
        if (!autoSequenceEnabled) return; // abort mid-animation if disabled
        const k = Math.min(1, (t - start) / durationMs);
        cam.position.lerpVectors(startPos, next, k);
        cam.lookAt(modelCenter);
        if (k < 1) requestAnimationFrame(tick); else {
          colorNextMesh();
          changeActiveItem();
          if (autoSequenceEnabled) {
            sequenceTimerId = setTimeout(step, 3000);
          }
        }
      };
      requestAnimationFrame(tick);
    }
    step();
  }

  function colorNextMesh() {
    if (!meshes.length) return;
    if (typeof colorNextMesh.index === 'undefined') colorNextMesh.index = -1;
    const prevIndex = colorNextMesh.index;
    let nextIndex = prevIndex;
    if (meshes.length === 1) {
      nextIndex = 0;
    } else {
      let tries = 0;
      while (tries < 10) {
        const rnd = Math.floor(Math.random() * meshes.length);
        if (rnd !== prevIndex) { nextIndex = rnd; break; }
        tries++;
      }
      if (nextIndex === prevIndex) nextIndex = (prevIndex + 1) % meshes.length;
    }
    colorNextMesh.index = nextIndex;
    highlightMeshAt(nextIndex);
  }

  function changeActiveItem() {
    const wrapper = rootEl.closest('.model3d-section') || document;
    const container = wrapper.querySelector('.model3d-section__items.swiper');
    if (!container || !container.swiper) return;
    container.swiper.slideNext();
  }

  function initSwiper() {
    const wrapper = rootEl.closest('.model3d-section') || document;
    const container = wrapper.querySelector('.model3d-section__items.swiper');
    if (!container) return;

    // Determine how many slides mamy w Swiperze, żeby zmapować meshe → slajdy.
    try {
      const slides = container.querySelectorAll('.model3d-section__item.swiper-slide');
      slideCount = (slides && slides.length) ? slides.length : 0;
    } catch (e) {
      slideCount = 0;
    }

    const swiper = new Swiper(container, {
      modules: [Autoplay, Pagination],
      direction: 'vertical',
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      allowTouchMove: false,
      speed: 600,
      autoHeight: false,
      // Autoplay wyłączony – slajdy zmieniają się tylko po kliknięciu ścianki/logo
      autoplay: false,
      pagination: {
        el: wrapper.querySelector('.model3d-section__progress'),
        type: 'progressbar',
        clickable: false
      },
      on: {
        afterInit: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} },
        slideChange: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} },
        resize: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} },
        imagesReady: function(){ try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {} }
      }
    });

    // Store reference for external control
    container.swiper = swiper;

    // Keep for potential external syncs in future
    try { if (window.wsLocoUpdate) window.wsLocoUpdate(); } catch (e) {}
  }

  function controlSwiper(action) {
    try {
      const wrapper = rootEl.closest('.model3d-section') || document;
      const container = wrapper.querySelector('.model3d-section__items.swiper');
      const swiper = container && container.swiper;
      if (!swiper) return;
      // Autoplay wyłączony – funkcja pozostawiona na przyszłość jako no-op
    } catch (e) {}
  }

  function enableAutoSequence(enable) {
    autoSequenceEnabled = !!enable;
    if (!autoSequenceEnabled && sequenceTimerId) {
      try { clearTimeout(sequenceTimerId); } catch (e) {}
      sequenceTimerId = null;
    }
    if (autoSequenceEnabled && hasInitialized) {
      animateCameraSequence(camera);
    }
  }

  function highlightMeshAt(index) {
    if (!meshes.length) return;
    const clamped = ((index | 0) % meshes.length + meshes.length) % meshes.length;
    const curr = meshes[clamped];
    if (!curr) return;
    setActiveMesh(curr);
  }

  function highlightRandomMeshDifferent() {
    if (!meshes.length) return;
    const current = typeof colorNextMesh.index === 'number' ? colorNextMesh.index : -1;
    let next = current;
    if (meshes.length === 1) {
      next = 0;
    } else {
      let tries = 0;
      while (tries < 10) {
        const rnd = Math.floor(Math.random() * meshes.length);
        if (rnd !== current) { next = rnd; break; }
        tries++;
      }
      if (next === current) next = (current + 1) % meshes.length;
    }
    colorNextMesh.index = next;
    highlightMeshAt(next);
  }

  function getBaseHex(mesh) {
    if (!mesh) return 0x000000;
    if (typeof mesh.currentHex === 'number') return mesh.currentHex;
    try {
      if (mesh.material && mesh.material.color) {
        return mesh.material.color.getHex();
      }
    } catch (e) {}
    return 0x000000;
  }

  function setHoveredMesh(mesh) {
    if (mesh === hoveredMesh) return;
    const prev = hoveredMesh;
    hoveredMesh = mesh;

    // Zdejmowanie hovera: poprzedni element wraca natychmiast do koloru bazowego
    if (prev && prev !== activeMesh) {
      try {
        const baseHex = getBaseHex(prev);
        prev.material.color.setHex(baseHex);
        prev.material.opacity = 0.8;
      } catch (e) {}
    }

    // Jeśli nie ma nowego hovera lub jest to element aktywny – kończymy
    if (!hoveredMesh || hoveredMesh === activeMesh) return;

    // Nałożenie hovera na nowy element – szary kolor, w pełni nieprzezroczysty
    try { hoveredMesh.material.opacity = 1; } catch (e) {}
    try { hoveredMesh.material.color.setHex(HOVER_HEX); } catch (e) {}
  }

  function setActiveMesh(mesh) {
    if (mesh === activeMesh) return;
    // Reset previous active color back to base (unless it's still hovered, which will reapply hover)
    if (activeMesh && activeMesh !== hoveredMesh) {
      fadeMeshColor(
        activeMesh,
        activeMesh.material.color.getHex(),
        getBaseHex(activeMesh),
        200
      );
      try { activeMesh.material.opacity = 0.8; } catch (e) {}
    }
    activeMesh = mesh;
    if (!activeMesh) return;
    // If something is both active and hovered, prefer the stronger active color
    try { activeMesh.material.opacity = 1; } catch (e) {}
    fadeMeshColor(
      activeMesh,
      activeMesh.material.color.getHex(),
      ACTIVE_HEX,
      200
    );
  }

  function focusOnMesh(mesh) {
    if (!mesh) return;
    // Mark mesh as active (strong highlight)
    setActiveMesh(mesh);

    // Jeśli mamy Swipera, ustaw odpowiedni slajd na podstawie indeksu mesha.
    try {
      const wrapper = rootEl.closest('.model3d-section') || document;
      const container = wrapper.querySelector('.model3d-section__items.swiper');
      const swiper = container && container.swiper;
      if (swiper && slideCount > 0) {
        const meshIndex = meshes.indexOf(mesh);
        if (meshIndex >= 0) {
          // Prosta mapa: kolejność meshów → kolejność slajdów (z zawinięciem modulo).
          const targetSlide = ((meshIndex % slideCount) + slideCount) % slideCount;
          if (typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(targetSlide);
          } else if (typeof swiper.slideTo === 'function') {
            swiper.slideTo(targetSlide);
          }
        }
      }
    } catch (e) {}

    // Compute a tighter bounding sphere for the clicked mesh
    const box = new THREE.Box3().setFromObject(mesh);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const center = sphere.center.clone();
    const radius = Math.max(sphere.radius, 0.001);

    // Re-center camera orbit and distance towards this mesh
    targetModelCenter.copy(center);
    const required = getRequiredDistance(camera, renderer, radius);
    targetBaseCameraRadius = required;
    // Keep current viewing direction; no sudden flips, only smooth slide handled in render()
    const currentOffset = camera.position.clone().sub(modelCenter);
    const spherical = new THREE.Spherical().setFromVector3(currentOffset);
    targetBaseCameraPhi = spherical.phi;
    targetBaseCameraTheta = spherical.theta;
  }

  function isSlashMesh(obj) {
    if (!obj) return false;
    let n = obj;
    // Szukamy w górę po drzewie węzłów nazwy zawierającej znak "/"
    try {
      while (n) {
        if (n.name && n.name.indexOf('/') !== -1) {
          return true;
        }
        n = n.parent;
      }
    } catch (e) {}
    return false;
  }

  function initOffcanvasHover() {
    if (!isInOffcanvas) return;
    try {
      const offcanvas = rootEl.closest('.offcanvas') || rootEl;
      const nav = offcanvas.querySelector('.offcanvas__nav');
      if (!nav) return;
      const links = nav.querySelectorAll('a');
      if (!links || !links.length) return;

      links.forEach(function(link){
        link.addEventListener('mouseenter', function(){
          enableAutoSequence(false);
          highlightRandomMeshDifferent();
        });
        link.addEventListener('focus', function(){
          enableAutoSequence(false);
          highlightRandomMeshDifferent();
        });
      });

      // Keep camera sequence disabled after hover ends in offcanvas
      nav.addEventListener('mouseleave', function(){
        enableAutoSequence(false);
      });
    } catch (e) {}
  }

  // Utilities
  function isWebGLAvailable() {
    try {
      var canvas = document.createElement('canvas');
      var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      return !!gl;
    } catch (e) {
      return false;
    }
  }
  function fitCameraToModel(cam, rend, model, opts) {
    const width = (canvasRoot && canvasRoot.clientWidth) || rootEl.clientWidth || 800;
    const height = (canvasRoot && canvasRoot.clientHeight) || rootEl.clientHeight || 500;
    const box = new THREE.Box3().setFromObject(model);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    modelCenter.copy(sphere.center);
    modelRadius = Math.max(sphere.radius, 0.001);

    const required = getRequiredDistance(cam, rend, modelRadius);
    // optionally keep current viewing direction
    let dir = new THREE.Vector3(1, 1, 1).normalize();
    if (opts && opts.maintainAngle) {
      dir = cam.position.clone().sub(modelCenter).normalize();
    }
    cam.position.copy(modelCenter.clone().add(dir.multiplyScalar(required)));
    cam.near = required / 100;
    cam.far = required * 100;
    cam.updateProjectionMatrix();
    cam.lookAt(modelCenter);
  }

  function getRequiredDistance(cam, rend, radius) {
    const width = (canvasRoot && canvasRoot.clientWidth) || rootEl.clientWidth || 800;
    const height = (canvasRoot && canvasRoot.clientHeight) || rootEl.clientHeight || 500;
    const vFov = THREE.MathUtils.degToRad(cam.fov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * (width / height));
    const distV = radius / Math.tan(vFov / 2);
    const distH = radius / Math.tan(hFov / 2);
    const required = Math.max(distV, distH) * 0.9; // padding
    return required;
  }

  function fadeMeshColor(mesh, fromHex, toHex, durationMs) {
    const from = new THREE.Color(fromHex);
    const to = new THREE.Color(toHex);
    const mat = mesh.material;
    const start = performance.now();
    function step(t) {
      const k = Math.min(1, (t - start) / durationMs);
      mat.color.r = from.r + (to.r - from.r) * k;
      mat.color.g = from.g + (to.g - from.g) * k;
      mat.color.b = from.b + (to.b - from.b) * k;
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  // Removed manual height syncing; Swiper autoHeight handles it

  // Page visibility pause/resume
  try {
    document.addEventListener('visibilitychange', function(){
      if (!hasInitialized) return;
      setVisible(!document.hidden && visibilityState);
    });
  } catch (e) {}

  // Optional LocomotiveScroll integration to reinforce visibility checks
  try {
    function locomotiveHook(scroll){
      if (!scroll || typeof scroll.on !== 'function') return;
      let ticking = false;
      scroll.on('scroll', function(){
        if (ticking) return; ticking = true;
        requestAnimationFrame(function(){
          const rect = rootEl.getBoundingClientRect();
          const vh = window.innerHeight || 0;
          const inView = rect.bottom > vh * 0.1 && rect.top < vh * 0.9;
          setVisible(inView);
          ticking = false;
        });
      });
    }
    if (window.wsScroll) locomotiveHook(window.wsScroll);
    window.addEventListener('ws:locomotive-ready', function(e){
      locomotiveHook((e && e.detail && e.detail.scroll) || window.wsScroll);
    }, { once: true });
  } catch (e) {}
}



